import { Button, Card, ConfirmDialog } from '@wundo/ui';
import type { Organisation } from '@/types/organisations.ts';
import type {
  NotificationBulkSend,
  NotificationRecipient,
} from '@/lib/notificationSend.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { KindFields } from './send/KindFields.tsx';
import { MeetingChoice } from './send/MeetingChoice.tsx';
import { PeopleList } from './send/PeopleList.tsx';
import { PreviewSection } from './send/PreviewSection.tsx';
import { RecipientFinder } from './send/RecipientFinder.tsx';
import { SendConfirmDialog } from './send/SendConfirmDialog.tsx';
import { CHANNEL_LABELS, nameOf, skipLabel } from './send/sendLabels.ts';
import { useSendForm } from './send/useSendForm.ts';

/*
 * Sending one of Wundo's own notifications by hand (docs/design/notification-console.md).
 * Keyed on the header's organisation, so choosing another one starts the form over.
 */
export function NotificationsTab() {
  const headerOrganisation = useOrganisationFilter();

  return (
    <NotificationsForm
      key={headerOrganisation?.id ?? 'all'}
      headerOrganisation={headerOrganisation}
    />
  );
}

function NotificationsForm({
  headerOrganisation,
}: {
  headerOrganisation: Organisation | null;
}) {
  const form = useSendForm(headerOrganisation);
  const { kind, options, recipients, selection, meeting, reach, sending } = form;
  const { recipient, people } = recipients;
  const { preview, previewRecipient } = form.previewState;

  return (
    <div className='flex flex-col gap-7'>
      <ReadingColumn className='flex flex-col gap-4'>
        <KindFields
          kinds={kind.kinds.data ?? []}
          kind={kind.kind}
          entry={kind.entry}
          isEmail={kind.isEmail}
          takesNeverName={options.takesNeverName}
          neverNameText={options.neverNameText}
          neverNameError={options.neverNameError}
          channel={options.channel}
          resend={options.resend}
          onKindChange={form.changeKind}
          onNeverNameChange={options.typeNeverName}
          onNeverNameBlur={options.applyNeverName}
          onChannelChange={form.chooseChannel}
          onResendChange={form.chooseResend}
        />

        {recipient ? (
          <RecipientCard recipient={recipient} onChange={form.clearRecipient} />
        ) : (
          <>
            <RecipientFinder
              headerOrganisation={headerOrganisation}
              search={recipients.search}
              roleplayLibraryId={
                options.takesRoleplay ? recipients.libraryOrganisationId : null
              }
              roleplayId={options.roleplayId ?? ''}
              loading={recipients.candidates.isFetching || recipients.picked.isFetching}
              onScopeChange={form.changeScope}
              onUserPick={form.pickUser}
              onOrganisationChange={form.changeOrganisation}
              onCohortChange={form.changeCohort}
              onRoleplayChange={form.chooseRoleplay}
            />
            {!recipients.candidates.isFetching && people.length > 0 && (
              <PeopleList
                people={people}
                isUserScope={kind.isUserScope}
                isEmail={kind.isEmail}
                selectedIds={selection.selectedIds}
                selectedCount={selection.selected.length}
                allSelected={selection.allSelected}
                reach={reach}
                canSend={form.canSendList}
                onToggleAll={selection.toggleAll}
                onToggleOne={selection.toggleOne}
                onPreview={selection.showPreviewFor}
                onSend={sending.openConfirm}
                onPick={form.pickFromList}
                onResubscribe={sending.openResubscribe}
              />
            )}
          </>
        )}
      </ReadingColumn>

      {sending.bulkResult && (
        <BulkResultCard
          result={sending.bulkResult}
          isEmail={kind.isEmail}
          people={people}
        />
      )}

      {recipient && (
        <MeetingChoice
          loading={meeting.meetings.isPending}
          meetings={meeting.meetings.data ?? []}
          value={meeting.meetingId}
          onChange={form.chooseMeeting}
        />
      )}

      {sending.lastSent !== '' && (
        <Card role='status' tone='quiet' className='border-accent-line text-sm text-ink'>
          {sending.lastSent}
        </Card>
      )}

      <PreviewSection
        canPreview={form.previewState.canPreview}
        loading={preview.isPending}
        preview={preview.data}
        recipient={previewRecipient}
        isUserScope={kind.isUserScope}
        isEmail={kind.isEmail}
        takesRoleplay={options.takesRoleplay}
        resend={options.resend}
        onSend={sending.openConfirm}
      />

      <ResubscribeDialog
        person={sending.dialog.kind === 'resubscribe' ? sending.dialog.person : null}
        failed={sending.resubscribe.isError}
        onClose={sending.closeDialog}
        onConfirm={sending.confirmResubscribe}
      />

      <SendConfirmDialog
        open={sending.dialog.kind === 'confirmSend'}
        error={sending.sendError}
        kindLabel={kind.label}
        isUserScope={kind.isUserScope}
        isEmail={kind.isEmail}
        channel={options.channel}
        resend={options.resend}
        takesRoleplay={options.takesRoleplay}
        reachableCount={reach.reachableCount}
        selectedCount={selection.selected.length}
        leftOutReasons={reach.selectedLeftOutReasons}
        resentCount={reach.resentCount}
        recipient={recipient}
        meeting={meeting.selectedMeeting}
        settled={preview.data?.delivery.settled}
        onClose={sending.closeDialog}
        onConfirm={sending.confirmSend}
      />
    </div>
  );
}

function RecipientCard({
  recipient,
  onChange,
}: {
  recipient: NotificationRecipient;
  onChange: () => void;
}) {
  return (
    <Card className='flex items-center justify-between gap-3'>
      <div className='min-w-0'>
        <p className='truncate text-sm font-semibold text-ink'>{nameOf(recipient)}</p>
        <p className='truncate text-sm text-ink-muted'>
          {recipient.email ?? 'No email address'}
          {recipient.organisation_name ? ` · ${recipient.organisation_name}` : ''}
          {` · ${CHANNEL_LABELS[recipient.notification_channel]}`}
        </p>
      </div>
      <Button variant='quiet' size='sm' onClick={onChange}>
        Change
      </Button>
    </Card>
  );
}

interface BulkResultCardProps {
  result: NotificationBulkSend;
  isEmail: boolean;
  /** The list the send was fired from, to name whoever it left out. */
  people: NotificationRecipient[];
}

function queuedSentence(result: NotificationBulkSend, isEmail: boolean): string {
  const people = result.queued === 1 ? 'person' : 'people';
  return isEmail
    ? `Sent to ${result.queued} ${people}. The History tab shows what became of each mail.`
    : `Queued for ${result.queued} ${people}. They receive it within a few minutes; the list above shows who has had it.`;
}

function BulkResultCard({ result, isEmail, people }: BulkResultCardProps) {
  const personOf = (userId: string) => people.find((row) => row.user_id === userId);

  return (
    <Card
      role='status'
      tone='quiet'
      className={result.skipped.length > 0 ? 'border-danger-line' : 'border-accent-line'}
    >
      <p className='text-sm font-semibold text-ink'>{queuedSentence(result, isEmail)}</p>
      {result.skipped.length > 0 && (
        <ul className='mt-2 list-inside list-disc text-sm text-ink-secondary'>
          {result.skipped.map((skip) => {
            const person = personOf(skip.user_id);
            return (
              <li key={skip.user_id}>
                {person ? nameOf(person) : skip.user_id} —{' '}
                {skipLabel(skip.reason, person)}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

interface ResubscribeDialogProps {
  /** Null while closed. */
  person: NotificationRecipient | null;
  failed: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | undefined;
}

function ResubscribeDialog({
  person,
  failed,
  onClose,
  onConfirm,
}: ResubscribeDialogProps) {
  return (
    <ConfirmDialog
      open={person !== null}
      onOpenChange={(open) => !open && onClose()}
      title={`Resubscribe ${person ? nameOf(person) : ''}?`}
      description='They get the emails they unsubscribed from again. Only do this when they asked for it.'
      confirmLabel='Resubscribe'
      cancelLabel='Cancel'
      error={failed ? 'Resubscribing failed. Try again.' : undefined}
      onConfirm={onConfirm}
    />
  );
}
